import type { AppDispatch, RootState } from '../store';
import {
  clearDxfImport,
  resumeDxfImportFromError,
  setDxfImportError,
  setDxfImportPhase,
  startDxfImport,
  startDxfOpen,
  type DxfImportPhase,
} from '../features/dxfImport/dxfImportSlice';
import {
  fetchBackendLosslessJsonLayout,
  importBackendDxfLayout,
  isRecoverableImportInterruptMessage,
  isTransientHeavyLoadError,
  recoverImportByProjectName,
} from './dxfApi';
import { applyLoadedFloorPlanToEditor } from './floorPlanLoader';
import { getFloorPlansApiBase, getImportsApiUrl } from './apiConfig';

const JSON_READY_ATTEMPTS = 24;
const JSON_READY_DELAY_MS = 5000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isCurrentGeneration(getState: () => RootState, generation: number): boolean {
  const state = getState().dxfImport;
  return state.active && state.generation === generation && state.phase !== 'error';
}

function reportStatus(
  dispatch: AppDispatch,
  getState: () => RootState,
  generation: number,
  phase: 'uploading' | 'fetching' | 'applying',
  message: string,
  progress?: number | null,
  forceProgress?: boolean
) {
  if (!isCurrentGeneration(getState, generation)) return;
  dispatch(setDxfImportPhase({ phase, message, progress, forceProgress }));
}

async function fetchPlanWhenReady(params: {
  dispatch: AppDispatch;
  getState: () => RootState;
  generation: number;
  floorPlanId: string;
  projectName: string;
  floorPlansBaseUrl: string;
}) {
  const { dispatch, getState, generation, floorPlanId, projectName, floorPlansBaseUrl } = params;

  let lastErr: unknown;
  for (let attempt = 0; attempt < JSON_READY_ATTEMPTS; attempt++) {
    if (!isCurrentGeneration(getState, generation)) return null;

    reportStatus(
      dispatch,
      getState,
      generation,
      'fetching',
      attempt === 0
        ? 'Loading floor plan geometry…'
        : `Server is still preparing geometry (${attempt + 1}/${JSON_READY_ATTEMPTS})…`
    );

    try {
      const plan = await fetchBackendLosslessJsonLayout({
        floorPlanId,
        baseUrl: floorPlansBaseUrl,
        fallbackName: projectName,
        onStatus: (message, progress) => {
          const currentMode = getState().dxfImport.mode;
          let mappedProgress: number | undefined;
          if (progress != null) {
            if (currentMode === 'open') {
              // Open mode: download 1%..85%, parsing 85%..95%
              if (/parsing/i.test(message)) {
                mappedProgress = 92;
              } else {
                mappedProgress = Math.min(85, Math.max(1, Math.round(1 + (progress / 100) * 84)));
              }
            } else {
              // Upload mode: download 75%..92%, parsing 92%..95%
              if (/parsing/i.test(message)) {
                mappedProgress = 94;
              } else {
                mappedProgress = Math.min(92, Math.max(75, Math.round(75 + (progress / 100) * 17)));
              }
            }
          } else if (/parsing/i.test(message)) {
            mappedProgress = currentMode === 'open' ? 92 : 94;
          }
          reportStatus(dispatch, getState, generation, 'fetching', message, mappedProgress);
        },
      });
      if (plan.polylines.length > 0) return plan;
      lastErr = new Error('No drawable geometry found in backend lossless JSON response.');
    } catch (err) {
      lastErr = err;
      // Plan may exist before lossless JSON is ready — keep polling on soft failures.
      if (!isTransientHeavyLoadError(err) && attempt > 2) {
        const msg = err instanceof Error ? err.message : String(err);
        if (!/404|not found|preparing|still/i.test(msg)) throw err;
      }
    }

    await sleep(JSON_READY_DELAY_MS);
  }

  if (lastErr instanceof Error) throw lastErr;
  throw new Error('Floor plan geometry was not ready in time. Please try Keep waiting again.');
}

async function finishImportWithFloorPlanId(params: {
  dispatch: AppDispatch;
  getState: () => RootState;
  generation: number;
  floorPlanId: string;
  projectName: string;
  floorPlansBaseUrl: string;
}): Promise<void> {
  const plan = await fetchPlanWhenReady(params);
  if (!plan) return;
  if (!isCurrentGeneration(params.getState, params.generation)) return;

  reportStatus(
    params.dispatch,
    params.getState,
    params.generation,
    'applying',
    'Drawing the floor plan onto the canvas…',
    96
  );

  await applyLoadedFloorPlanToEditor(params.dispatch, plan, {
    floorPlanId: params.floorPlanId,
  });

  if (!isCurrentGeneration(params.getState, params.generation)) return;
  reportStatus(
    params.dispatch,
    params.getState,
    params.generation,
    'applying',
    'Ready!',
    100
  );
  await sleep(1200);
  if (!isCurrentGeneration(params.getState, params.generation)) return;
  params.dispatch(clearDxfImport());
}

/**
 * Open an already-uploaded floor plan: fetch lossless JSON in the background
 * while the editor shell is already open. Callers should open a shell floor plan first.
 */
export function runDxfOpenExisting(params: {
  dispatch: AppDispatch;
  getState: () => RootState;
  floorPlanId: string;
  projectName: string;
  floorPlansBaseUrl?: string;
}): void {
  const { dispatch, getState, floorPlanId } = params;
  const projectName = params.projectName.trim();
  if (!floorPlanId.trim() || !projectName) return;

  dispatch(
    startDxfOpen({
      projectName,
    })
  );

  const generation = getState().dxfImport.generation;
  const floorPlansBaseUrl = params.floorPlansBaseUrl ?? getFloorPlansApiBase();

  void (async () => {
    try {
      if (!isCurrentGeneration(getState, generation)) return;

      await finishImportWithFloorPlanId({
        dispatch,
        getState,
        generation,
        floorPlanId: floorPlanId.trim(),
        projectName,
        floorPlansBaseUrl,
      });
    } catch (err) {
      if (getState().dxfImport.generation !== generation) return;
      if (!getState().dxfImport.active) return;
      const message = err instanceof Error ? err.message : String(err);
      dispatch(setDxfImportError(message));
    }
  })();
}

/**
 * Runs the existing DXF upload → lossless JSON → apply pipeline in the background
 * while the editor is already open. Callers should open a shell floor plan first.
 */
export function runDxfImport(params: {
  dispatch: AppDispatch;
  getState: () => RootState;
  file: File;
  projectName: string;
  importsUrl?: string;
  floorPlansBaseUrl?: string;
}): void {
  const { dispatch, getState, file } = params;
  const projectName = params.projectName.trim();
  if (!file || !projectName) return;

  dispatch(
    startDxfImport({
      fileName: file.name,
      projectName,
    })
  );

  const generation = getState().dxfImport.generation;
  const importsUrl = params.importsUrl ?? getImportsApiUrl();
  const floorPlansBaseUrl = params.floorPlansBaseUrl ?? getFloorPlansApiBase();
  const startedAtMs = Date.now();

  void (async () => {
    try {
      if (!isCurrentGeneration(getState, generation)) return;

      const uploadResult = await importBackendDxfLayout({
        file,
        projectName,
        url: importsUrl,
        onStatus: (message, progress, stage) => {
          let currentProgress: number | undefined;
          if (stage === 'upload') {
            // First import POST call: show only 0% (no upload progress 1..100)
            currentProgress = 0;
          } else if (progress != null) {
            // Second call: GET /imports/{job_id} API returns progress value, use that directly!
            currentProgress = Math.min(100, Math.max(0, Math.round(progress)));
          }
          const phase: Exclude<DxfImportPhase, 'error'> =
            stage === 'processing' ? 'fetching' : 'uploading';
          reportStatus(
            dispatch,
            getState,
            generation,
            phase,
            message,
            currentProgress,
            true
          );
        },
      });

      if (!isCurrentGeneration(getState, generation)) return;

      await finishImportWithFloorPlanId({
        dispatch,
        getState,
        generation,
        floorPlanId: uploadResult.floor_plan_id,
        projectName,
        floorPlansBaseUrl,
      });
    } catch (err) {
      if (getState().dxfImport.generation !== generation) return;
      if (!getState().dxfImport.active) return;

      // One more recovery pass if the upload socket dropped after our first poll window.
      if (
        (err instanceof Error && isRecoverableImportInterruptMessage(err.message)) ||
        isTransientHeavyLoadError(err)
      ) {
        try {
          reportStatus(
            dispatch,
            getState,
            generation,
            'uploading',
            'Upload may have finished — checking the server again…'
          );
          const recovered = await recoverImportByProjectName(
            projectName,
            startedAtMs,
            (message, progress) => {
              const currentProgress =
                progress != null
                  ? Math.min(100, Math.max(1, Math.round(progress)))
                  : undefined;
              reportStatus(dispatch, getState, generation, 'fetching', message, currentProgress, true);
            }
          );
          if (recovered && isCurrentGeneration(getState, generation)) {
            await finishImportWithFloorPlanId({
              dispatch,
              getState,
              generation,
              floorPlanId: recovered.floor_plan_id,
              projectName,
              floorPlansBaseUrl,
            });
            return;
          }
        } catch (recoverErr) {
          if (getState().dxfImport.generation !== generation) return;
          if (!getState().dxfImport.active) return;
          const message =
            recoverErr instanceof Error ? recoverErr.message : String(recoverErr);
          dispatch(setDxfImportError(message));
          return;
        }
      }

      const message = err instanceof Error ? err.message : String(err);
      dispatch(setDxfImportError(message));
    }
  })();
}

/**
 * After a dropped upload connection, poll for the already-created floor plan and
 * load geometry — does **not** re-POST the DXF (avoids duplicates).
 */
export function continueDxfImportRecovery(params: {
  dispatch: AppDispatch;
  getState: () => RootState;
  floorPlansBaseUrl?: string;
}): void {
  const { dispatch, getState } = params;
  const { projectName, fileName } = getState().dxfImport;
  if (!projectName.trim()) return;

  dispatch(
    resumeDxfImportFromError({
      phase: 'uploading',
      message: 'Checking whether the upload already finished on the server…',
    })
  );

  const generation = getState().dxfImport.generation;
  const floorPlansBaseUrl = params.floorPlansBaseUrl ?? getFloorPlansApiBase();
  // Accept plans created in the last 30 minutes (covers long backend processing).
  const startedAtMs = Date.now() - 30 * 60_000;

  void (async () => {
    try {
      const recovered = await recoverImportByProjectName(
        projectName,
        startedAtMs,
        (message, progress) => {
          const currentProgress =
            progress != null
              ? Math.min(100, Math.max(1, Math.round(progress)))
              : undefined;
          reportStatus(dispatch, getState, generation, 'fetching', message, currentProgress, true);
        }
      );

      if (!isCurrentGeneration(getState, generation)) return;

      if (!recovered) {
        dispatch(
          setDxfImportError(
            `Still waiting for “${projectName}” (${fileName || 'DXF'}). The upload may still be processing — try Keep waiting again in a minute, or open it from the project list if it appears.`
          )
        );
        return;
      }

      await finishImportWithFloorPlanId({
        dispatch,
        getState,
        generation,
        floorPlanId: recovered.floor_plan_id,
        projectName,
        floorPlansBaseUrl,
      });
    } catch (err) {
      if (getState().dxfImport.generation !== generation) return;
      if (!getState().dxfImport.active) return;
      const message = err instanceof Error ? err.message : String(err);
      dispatch(setDxfImportError(message));
    }
  })();
}
